// ============================================================================
//  src/services/firestore.js
//  Camada de servicos Firestore — Jatear Gestao de Tintas
//  Bloco 3.1 — CRUD generico reutilizavel por todos os cadastros
//
//  Firebase 12.x — API modular
//  Padroniza: tratamento de erro, ordenacao, timestamps, tempo real opcional.
// ============================================================================

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

// ----------------------------------------------------------------------------
//  Helper interno — normaliza erros do Firestore em mensagem amigavel pt-BR
// ----------------------------------------------------------------------------
function tratarErro(operacao, colecao, erro) {
  // Log tecnico no console (para depuracao do desenvolvedor)
  console.error(`[Firestore][${operacao}][${colecao}]`, erro);

  const codigo = erro?.code || '';
  const mapa = {
    'permission-denied':
      'Sem permissao para esta operacao. Verifique seu perfil de acesso.',
    unavailable:
      'Servico indisponivel no momento. Verifique a conexao e tente novamente.',
    'not-found': 'Registro nao encontrado.',
    'already-exists': 'Ja existe um registro com este identificador.',
    'failed-precondition':
      'Operacao nao permitida no estado atual dos dados.',
  };
  const mensagem =
    mapa[codigo] ||
    'Ocorreu um erro ao acessar os dados. Tente novamente em instantes.';

  // Lança um erro "limpo" para a tela tratar (mostrar toast/alerta)
  const e = new Error(mensagem);
  e.codigoOriginal = codigo;
  e.operacao = operacao;
  e.colecao = colecao;
  throw e;
}

// ----------------------------------------------------------------------------
//  LISTAR — retorna todos os documentos de uma colecao
//  options: { ordenarPor?: string, direcao?: 'asc'|'desc', filtros?: [] }
//  filtros: array de [campo, operador, valor] — ex: [['ativo','==',true]]
// ----------------------------------------------------------------------------
export async function listar(nomeColecao, options = {}) {
  try {
    const { ordenarPor, direcao = 'asc', filtros = [] } = options;
    const ref = collection(db, nomeColecao);

    const clausulas = [];
    for (const [campo, operador, valor] of filtros) {
      clausulas.push(where(campo, operador, valor));
    }
    if (ordenarPor) clausulas.push(orderBy(ordenarPor, direcao));

    const q = clausulas.length ? query(ref, ...clausulas) : ref;
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (erro) {
    tratarErro('listar', nomeColecao, erro);
  }
}

// ----------------------------------------------------------------------------
//  BUSCAR POR ID — retorna um documento ou null
// ----------------------------------------------------------------------------
export async function buscarPorId(nomeColecao, id) {
  try {
    const snap = await getDoc(doc(db, nomeColecao, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (erro) {
    tratarErro('buscarPorId', nomeColecao, erro);
  }
}

// ----------------------------------------------------------------------------
//  CRIAR — adiciona documento com ID automatico
//  Injeta criadoEm / atualizadoEm automaticamente
//  Retorna o id gerado
// ----------------------------------------------------------------------------
export async function criar(nomeColecao, dados) {
  try {
    const ref = await addDoc(collection(db, nomeColecao), {
      ...dados,
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
    return ref.id;
  } catch (erro) {
    tratarErro('criar', nomeColecao, erro);
  }
}

// ----------------------------------------------------------------------------
//  CRIAR COM ID — grava documento com ID definido (ex: usuarios usa UID)
// ----------------------------------------------------------------------------
export async function criarComId(nomeColecao, id, dados) {
  try {
    await setDoc(doc(db, nomeColecao, id), {
      ...dados,
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
    return id;
  } catch (erro) {
    tratarErro('criarComId', nomeColecao, erro);
  }
}

// ----------------------------------------------------------------------------
//  ATUALIZAR — atualiza campos de um documento existente
//  Atualiza atualizadoEm automaticamente; NUNCA mexe em criadoEm
// ----------------------------------------------------------------------------
export async function atualizar(nomeColecao, id, dados) {
  try {
    const limpo = { ...dados };
    delete limpo.id;
    delete limpo.criadoEm; // protege o timestamp de criacao
    await updateDoc(doc(db, nomeColecao, id), {
      ...limpo,
      atualizadoEm: serverTimestamp(),
    });
    return true;
  } catch (erro) {
    tratarErro('atualizar', nomeColecao, erro);
  }
}

// ----------------------------------------------------------------------------
//  REMOVER — apaga um documento
//  ATENCAO: regra de negocio do projeto — historico de movimentacao
//  NUNCA usa esta funcao (movimentacoes sao imutaveis). Usar so em
//  cadastros (produtos, resinas, etc.).
// ----------------------------------------------------------------------------
export async function remover(nomeColecao, id) {
  try {
    await deleteDoc(doc(db, nomeColecao, id));
    return true;
  } catch (erro) {
    tratarErro('remover', nomeColecao, erro);
  }
}

// ----------------------------------------------------------------------------
//  OUVIR (tempo real) — assina mudancas de uma colecao em tempo real
//  Uso futuro: telas concorridas (Estoque, Movimentacao — Bloco 4)
//  Retorna a funcao de "unsubscribe" — chamar no cleanup do useEffect
//
//  Exemplo de uso na tela:
//    useEffect(() => {
//      const off = ouvir('produtos', setLista, { ordenarPor: 'descricao' });
//      return off;            // limpa o listener ao desmontar
//    }, []);
// ----------------------------------------------------------------------------
export function ouvir(nomeColecao, callback, options = {}) {
  const { ordenarPor, direcao = 'asc', filtros = [] } = options;
  const ref = collection(db, nomeColecao);

  const clausulas = [];
  for (const [campo, operador, valor] of filtros) {
    clausulas.push(where(campo, operador, valor));
  }
  if (ordenarPor) clausulas.push(orderBy(ordenarPor, direcao));

  const q = clausulas.length ? query(ref, ...clausulas) : ref;

  const unsubscribe = onSnapshot(
    q,
    (snap) => {
      const itens = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(itens);
    },
    (erro) => {
      console.error(`[Firestore][ouvir][${nomeColecao}]`, erro);
      // Em tempo real nao lancamos excecao (quebraria a UI);
      // entrega lista vazia e loga. A tela decide como sinalizar.
      callback([]);
    }
  );

  return unsubscribe;
}

// ----------------------------------------------------------------------------
//  Nomes das colecoes — fonte unica de verdade (evita typo em string solta)
//  Espelha o planejamento de colecoes do projeto.
// ----------------------------------------------------------------------------
export const COLECOES = {
  USUARIOS: 'usuarios',
  PRODUTOS: 'produtos',
  RESINAS: 'resinas',
  ESTOQUES: 'estoques',
  CLIENTES: 'clientes',
  ESTOQUE_POSICAO: 'estoque_posicao',
  ESTOQUE_LIMITES: 'estoque_limites',
  ESTOQUE_RESPONSAVEIS: 'estoque_responsaveis',
  MOVIMENTACOES: 'movimentacoes',
  ACEITES_MOVIMENTACAO: 'aceites_movimentacao',
  ORDENS_SERVICO: 'ordens_servico',
  OS_MATERIAIS: 'os_materiais',
  CONFIG_NOTIFICACOES: 'config_notificacoes',
  CONFIG_PERMISSOES: 'config_permissoes',
  PARAMETROS: 'parametros',
};
